import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, ModalSubTitle, SuggestModifiersChecker, useAssessmentColumns } from '../comps';
import { Poppins, Spacer, TableSkeleton } from 'src/common';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import Button from 'src/components/form/Button';
import { Guard } from '../Guard';
import { useScenarioColumns } from './useScenarioColumns';
import GenericTable from 'src/components/GenericTable';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { Back } from 'src/assets/dynamicSvg';
import {
  useAssessmentsQuery,
  useControlLinkedScenariosQuery,
  useLinkFrameworkControlsToScenariosMutation,
} from 'src/graphql/_generated';
import { KeyType, useCache } from 'src/graphql';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import { isQueryLoading } from 'src/graphql';

const StyledModal = styled(Modal)`
  width: 1350px;
  min-height: 200px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  .back {
    display: flex;
    align-items: center;
    cursor: pointer;

    &__text {
      font-size: 18px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.cflowerBlue};
      margin-left: 13px;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

interface LinkScenariosToControlModalProps {
  onClose: () => void;
  controlId: string;
  invalidateQueries: KeyType[];
  subTitle?: {
    title: string;
    value: string;
  };
  isModifierStrategyType: boolean;
}

export const LinkScenariosToControlModal: React.FC<LinkScenariosToControlModalProps> = ({
  onClose,
  controlId,
  invalidateQueries,
  subTitle,
  isModifierStrategyType,
}) => {
  const [err, setErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [addRecommendedModifiers, setAddRecommendedModifiers] = useState(false);
  const { refetchQuery } = useCache();

  const { data: { assessments = [] } = {}, loading: isAssessmentsLoading } = useAssessmentsQuery();

  const {
    data: controlLinkedScenariosData,
    loading: controlLinkedScenariosLoading,
    networkStatus: controlLinkedScenariosNetworkStatus,
  } = useControlLinkedScenariosQuery({
    variables: { id: controlId },
  });

  const isControlLinkedScenariosLoading = isQueryLoading({
    loading: controlLinkedScenariosLoading,
    data: controlLinkedScenariosData,
    networkStatus: controlLinkedScenariosNetworkStatus,
  });

  // Extract linked scenario IDs from the control's risk management plans
  const linkedScenarioIds = useMemo(() => {
    return (
      controlLinkedScenariosData?.control?.riskManagementPlans
        .map((plan) => plan.scenario?.id)
        .filter(Boolean) as string[] | undefined
    ) || [];
  }, [controlLinkedScenariosData]);

  const {
    columns: columnsAssessment,
    GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedAssessment,
    selectedAssessment,
    setSelectedAssessment,
  } = useAssessmentColumns({
    data: assessments,
  });

  const { columns, GTColumnSelectAnchorExported, checked, resetChecked } = useScenarioColumns({
    data: assessments.find((el) => el.id === selectedAssessment)?.scenarios || [],
    linkedScenarios: linkedScenarioIds,
    controlId,
  });

  const [isProcessing, setIsProcessing] = useState(false);

  const [linkFrameworkControlsToScenarios] = useLinkFrameworkControlsToScenariosMutation({
    onError: (err: any) => {
      setErr(err.message || 'Something went wrong');
      setIsProcessing(false);
    },
  });

  const afterSuccess = async () => {
    queryCache.invalidateQueries([QKeys.Audit]);
    await refetchQuery({ include: invalidateQueries });
    showSuccessToast(toastMessages.scenario.linked);
    onClose();
  };

  const isModalLoading = isAssessmentsLoading || isControlLinkedScenariosLoading;

  const isDisabledModSuggest = useMemo(() => {
    return !checked.length;
  }, [checked]);

  useEffect(() => {
    if (isDisabledModSuggest && addRecommendedModifiers) {
      setAddRecommendedModifiers(false);
    }
  }, [isDisabledModSuggest, addRecommendedModifiers, setAddRecommendedModifiers]);

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={!!checked.length}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen center={false} persistent={isProcessing}>
          <Spacer $px={40} />

          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              {selectedAssessment ? 'Select one or more scenarios to link' : 'Select Assessment'}
            </Poppins>
            {subTitle && <ModalSubTitle title={subTitle.title} value={subTitle.value} />}

            {selectedAssessment ? (
              <>
                <Spacer $px={10} />
                <div
                  className="back"
                  onClick={() => {
                    setSelectedAssessment('');
                    resetChecked();
                  }}
                >
                  <Back />
                  <Poppins className="back__text">Back</Poppins>
                </div>
                <Spacer $px={20} />
              </>
            ) : (
              <Spacer $px={20} />
            )}

            {selectedAssessment ? (
              <>
                <GenericTable
                  tableId={TableIds.controlLinkScenarioScenario}
                  data={assessments.find((el) => el.id === selectedAssessment)?.scenarios || []}
                  columns={columns}
                  GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
                  itemHeight={50}
                  searchable={['name', 'source', 'event', 'consequence', 'expected_loss', 'updated_at']}
                  rowDisabled={(row) => linkedScenarioIds.includes(row.id)}
                />
                {isModifierStrategyType && (
                  <SuggestModifiersChecker
                    isDisabledModSuggest={isDisabledModSuggest}
                    addRecommendedModifiers={addRecommendedModifiers}
                    setAddRecommendedModifiers={setAddRecommendedModifiers}
                  />
                )}
              </>
            ) : (
              <>
                {!isModalLoading ? (
                  <GenericTable
                    tableId={TableIds.controlLinkScenarioAssessment}
                    data={assessments}
                    columns={columnsAssessment}
                    GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedAssessment}
                    itemHeight={50}
                    rowDisabled={(row) => row.scenarios.length === 0}
                    searchable={['name', 'description']}
                  />
                ) : (
                  <TableSkeleton />
                )}
              </>
            )}
          </div>

          {selectedAssessment && (
            <>
              <Spacer $px={30} />
              <div className="h-padding">
                <Button
                  disabled={isProcessing || !checked.length}
                  loading={isProcessing}
                  onClick={() => {
                    setIsProcessing(true);
                    linkFrameworkControlsToScenarios({
                      variables: {
                        frameworkControlScenarioLinks: checked.map((id) => ({
                          frameworkControlId: controlId,
                          scenarioId: id,
                        })),
                        addRecommendedModifiers,
                      },
                    }).then(afterSuccess);

                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Confirm',
                      modal: 'Link scenarios to control modal',
                    });
                  }}
                  primary
                  css="width: 100%;"
                  data-cy="submit-btn"
                >
                  CONFIRM
                </Button>

                {!!err && (
                  <>
                    <Spacer $px={15} />
                    <div className="error">{err}</div>
                  </>
                )}
              </div>
            </>
          )}
          <Spacer $px={30} />
        </StyledModal>
      }
    />
  );
};
